import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, CalendarClock, Mail, Users } from 'lucide-react';
import {
  usePatient,
  usePatientClinics,
  usePatientReports,
  useSubscriptions,
} from '@/hooks/queries';
import { ClinicLabel } from '@/components/ClinicLabel';

/**
 * Patient details block for the global header strip (below the name, under the
 * motivations / dietary-restrictions row rendered by PatientTagsHeaderRow).
 *
 * Renders the key metadata (email, start date, next/last report, clinics) as a
 * compact INLINE row in the same visual style as the motivations/restrictions
 * strip — clinics last since it can be long — followed by a narrow "Requiring
 * Action" section. Goals + Medications are intentionally omitted here (Goals
 * live on their own tab; the drawer/rail that hosted them is gone).
 *
 * Self-sufficient — takes only `patientId` and fetches everything else. TanStack
 * Query dedupes against the same hooks the tab content uses, so no double-fetch.
 */
type Props = {
  patientId: number;
};

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
};

export const PatientRail = ({ patientId }: Props) => {
  const { t } = useTranslation();

  const patientQuery = usePatient(patientId);
  const patientClinicsQuery = usePatientClinics(patientId);
  const reportsQ = usePatientReports(patientId);
  const subscriptionsQ = useSubscriptions(patientId);

  const patient = patientQuery.data;

  const clinics = useMemo(
    () =>
      patientClinicsQuery.data?.map((c) => ({ id: c.id, name: c.name })) ??
      patient?.clinics?.map((c) => ({ id: c.id, name: c.name })) ??
      [],
    [patientClinicsQuery.data, patient],
  );

  const lastReport = useMemo(() => {
    const list = reportsQ.data ?? [];
    if (list.length === 0) return null;
    return [...list].sort((a, b) =>
      (b.report_date ?? b.date_until).localeCompare(a.report_date ?? a.date_until),
    )[0];
  }, [reportsQ.data]);

  // "Next report" approximation — the active subscription's expiration date,
  // since that's when the natural review cycle resets. Null when nothing's
  // active. NOT a true scheduled-next-report value (MNT doesn't surface one).
  const nextReportDate = useMemo(() => {
    const subs = subscriptionsQ.data ?? [];
    const today = new Date().toISOString().slice(0, 10);
    const active = subs
      .filter((s) => s.start_date <= today && (s.expiration_date ?? '') >= today)
      .sort((a, b) => (a.expiration_date ?? '').localeCompare(b.expiration_date ?? ''));
    return active[0]?.expiration_date ?? null;
  }, [subscriptionsQ.data]);

  // Earliest known start date across all subscriptions; falls back to
  // tracking_start_date when there are none.
  const startDate = useMemo(() => {
    const subs = subscriptionsQ.data ?? [];
    if (subs.length > 0) {
      return (
        [...subs]
          .map((s) => s.start_date)
          .filter((d): d is string => !!d)
          .sort()[0] ?? null
      );
    }
    return patient?.tracking_start_date ?? patient?.most_recent_subscription_start_date ?? null;
  }, [subscriptionsQ.data, patient]);

  return (
    // Details — inline, same style as the motivations/dietary-restrictions
    // strip, on their own line. Clinics last since it can be long. (Requiring
    // Action moved up to the global header via RequiringActionIndicator.)
    <div className="flex flex-wrap items-center gap-x-5 gap-y-0.5 text-xs">
      <DetailItem icon={Mail} label={t('Email')} value={patient?.email ?? '—'} />
        <DetailItem icon={Calendar} label={t('Start date')} value={formatDate(startDate)} />
        <DetailItem
          icon={CalendarClock}
          label={t('Next report')}
          value={formatDate(nextReportDate)}
        />
        <DetailItem
          icon={Calendar}
          label={t('Last report')}
          value={formatDate(lastReport?.report_date ?? lastReport?.date_until ?? null)}
        />
        <DetailItem
          icon={Users}
          label={t('Clinics')}
          truncate={false}
          value={
            clinics.length
              ? clinics.map((c, i) => (
                  <span key={c.id}>
                    {i > 0 && ', '}
                    <ClinicLabel name={c.name} id={c.id} />
                  </span>
                ))
              : '—'
          }
        />
    </div>
  );
};

/* -------------------------------------------------------------------------- */

const DetailItem = ({
  icon: Icon,
  label,
  value,
  truncate = true,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: React.ReactNode;
  truncate?: boolean;
}) => (
  <span className="inline-flex min-w-0 items-center gap-1.5">
    <Icon className="h-3.5 w-3.5 shrink-0 text-primary" />
    <span className="font-medium text-foreground">{label}:</span>
    <span className={truncate ? 'truncate text-muted-foreground' : 'text-muted-foreground'}>
      {value}
    </span>
  </span>
);
